import {
  Alert,
  Button,
  EmptyState,
  ResponsiveList,
  Skeleton,
  formatDate,
  type ResponsiveListColumn,
} from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';

import {
  downloadDeliveryNotePdf,
  listDeliveryNotes,
  requestSignedScanDownloadUrl,
  type DeliveryNote,
  type DeliveryNoteSearchParams,
  type DeliveryNoteSearchResult,
  type DeliveryNoteStatus,
} from '../../delivery-notes/api';
import { toPortalStatusLabel } from '../../delivery-notes/status-label';
import { usePortalMe } from '../../shell/use-portal-me';

const PAGE_SIZE = 50;

/** "En reparto" is what the list shows for both PENDING and DISPATCHED notes, so it queries both. */
const IN_TRANSIT = 'IN_TRANSIT';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: IN_TRANSIT, label: 'En reparto' },
  { value: 'DELIVERED', label: 'Entregado' },
  { value: 'SIGNED', label: 'Certificado' },
  { value: 'INVOICED', label: 'Facturado' },
  { value: 'VOID', label: 'Anulado' },
];

async function listNotesByStatus(
  filter: Omit<DeliveryNoteSearchParams, 'status'>,
  status: string,
): Promise<DeliveryNoteSearchResult> {
  if (status !== IN_TRANSIT) {
    return listDeliveryNotes({ ...filter, ...(status !== '' ? { status: status as DeliveryNoteStatus } : {}) });
  }

  const [pending, dispatched] = await Promise.all([
    listDeliveryNotes({ ...filter, status: 'PENDING' }),
    listDeliveryNotes({ ...filter, status: 'DISPATCHED' }),
  ]);

  return {
    ...pending,
    items: [...pending.items, ...dispatched.items].sort((a, b) =>
      b.serviceDate.localeCompare(a.serviceDate),
    ),
    total: pending.total + dispatched.total,
  };
}

/**
 * `portal-delivery-notes/spec.md` → "Pantalla de remitos en el portal
 * cliente": listado filtrable por fecha, sede y estado, columnas Número,
 * Fecha de entrega, Sede, Estado y descarga de PDF, mismo patrón que
 * `orders/order-list-page.tsx`.
 */
export function PortalDeliveryNotesPage(): JSX.Element {
  const me = usePortalMe(true);
  const [siteId, setSiteId] = useState('');
  const [status, setStatus] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  const [downloadingId, setDownloadingId] = useState<string | undefined>(undefined);
  const [downloadingScanId, setDownloadingScanId] = useState<string | undefined>(undefined);

  const filter = {
    ...(siteId !== '' ? { siteId } : {}),
    ...(serviceDate !== '' ? { serviceDateFrom: serviceDate, serviceDateTo: serviceDate } : {}),
    page: 1,
    pageSize: PAGE_SIZE,
  };

  const notes = useQuery({
    queryKey: ['portal-delivery-notes', filter, status],
    queryFn: () => listNotesByStatus(filter, status),
  });

  const sites = me.data?.status === 'ACTIVE' ? me.data.sites : [];

  async function handleDownload(id: string, noteNumber: string): Promise<void> {
    setDownloadError(undefined);
    setDownloadingId(id);

    try {
      await downloadDeliveryNotePdf(id, noteNumber);
    } catch {
      setDownloadError('No pudimos descargar el PDF del remito.');
    } finally {
      setDownloadingId(undefined);
    }
  }

  async function handleDownloadSignedScan(id: string): Promise<void> {
    setDownloadError(undefined);
    setDownloadingScanId(id);

    // Opened before the request: phones block a window opened after an await.
    const popup = window.open('', '_blank');

    try {
      const result = await requestSignedScanDownloadUrl(id);

      if (popup !== null) {
        popup.location.href = result.downloadUrl;
      } else {
        window.location.href = result.downloadUrl;
      }
    } catch {
      popup?.close();
      setDownloadError('No pudimos descargar el remito firmado.');
    } finally {
      setDownloadingScanId(undefined);
    }
  }

  function renderStatus(note: DeliveryNote): JSX.Element {
    return (
      <span className="text-sm text-slate-900">
        {toPortalStatusLabel(note.status, {
          receiverName: note.signature?.receiverName,
          signedAt: note.signature?.signedAt,
          voidReason: note.voidReason,
        })}
      </span>
    );
  }

  function renderActions(note: DeliveryNote): JSX.Element {
    return (
      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          className="w-full"
          loading={downloadingId === note.id}
          onClick={() => void handleDownload(note.id, note.noteNumber)}
        >
          Descargar PDF
        </Button>
        {note.status === 'SIGNED' ? (
          <Button
            variant="secondary"
            className="w-full"
            loading={downloadingScanId === note.id}
            onClick={() => void handleDownloadSignedScan(note.id)}
          >
            Descargar remito firmado
          </Button>
        ) : null}
      </div>
    );
  }

  const columns: ResponsiveListColumn<DeliveryNote>[] = [
    { key: 'noteNumber', header: 'Número', render: (note) => note.noteNumber },
    {
      key: 'serviceDate',
      header: 'Fecha de entrega',
      render: (note) => formatDate(note.serviceDate),
    },
    { key: 'siteNameSnapshot', header: 'Sede', render: (note) => note.siteNameSnapshot },
    { key: 'status', header: 'Estado', render: renderStatus },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold text-slate-900">Remitos</h1>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-service-date" className="text-sm font-medium text-slate-900">
            Fecha de entrega
          </label>
          <input
            id="filter-service-date"
            type="date"
            value={serviceDate}
            onChange={(event) => setServiceDate(event.target.value)}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-site" className="text-sm font-medium text-slate-900">
            Sede
          </label>
          <select
            id="filter-site"
            value={siteId}
            onChange={(event) => setSiteId(event.target.value)}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="">Todas las sedes</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-status" className="text-sm font-medium text-slate-900">
            Estado
          </label>
          <select
            id="filter-status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="">Todos los estados</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {downloadError !== undefined ? <Alert tone="danger">{downloadError}</Alert> : null}

      {notes.isPending ? (
        <div data-testid="portal-delivery-notes-skeleton" className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {notes.isError ? (
        <Alert tone="danger">
          No pudimos cargar los remitos.
          <div className="mt-2">
            <Button size="sm" onClick={() => void notes.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {notes.data !== undefined ? (
        <ResponsiveList
          items={notes.data.items}
          rowKey={(note) => note.id}
          columns={columns}
          renderCardHeader={(note) => (
            <div className="flex items-center justify-between gap-2">
              <span>{note.noteNumber}</span>
              <span className="font-normal text-slate-500">{formatDate(note.serviceDate)}</span>
            </div>
          )}
          renderCardBody={(note) => (
            <>
              <span className="text-slate-500">Sede</span>
              <span>{note.siteNameSnapshot}</span>
              <span className="col-span-2">{renderStatus(note)}</span>
            </>
          )}
          renderActions={renderActions}
          emptyState={
            <EmptyState
              headingLevel="h2"
              title="No se encontraron remitos."
              description="Probá con otra sede, estado o fecha."
            />
          }
          caption="Remitos"
          getRowTestId={(note) => `delivery-note-card-${note.id}`}
        />
      ) : null}
    </div>
  );
}
